import { Ray } from "@babylonjs/core/Culling/ray";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { InteractorManager } from "../../core/tool/InteractorManager";
import { getDataPath, Log } from "../../utils";
import { Interactor } from "./Interactor";
import { prepareSprites } from "./InteractorSprites";
export class Interactors {
    get manager() {
        return (this.mMngr);
    }
    get article() {
        return (this.mArticle);
    }
    constructor(pArticle, pManager) {
        this.onInstantVisibilityCheck = () => {
            this.mVisCheckTimeout = null;
            const tTime = Date.now();
            this.checkInteractorVisibilities();
            const tElapsedTime = Date.now() - tTime;
            //disable instant check if performance is bad
            if (tElapsedTime > 50)
                this.mInstantVisCheck = false;
        };
        this.onVisibilityCheck = () => {
            this.mVisCheckTimeout = null;
            this.checkInteractorVisibilities();
        };
        this.pickPredicate = (pMesh) => {
            return (pMesh != null && pMesh[Interactors.PICKING_ID] === this.mCurrentPickingId);
        };
        this.mMngr = pManager;
        this.mArticle = pArticle;
        this.mInteractors = new InteractorManager(this.mMngr.app.viewer);
        this.mInteractors.enabled = false;
        this.mGetInteractorsPromise = null;
        this.mVisCheckTimeout = null;
        this.mDisableRequestVisCheck = false;
        this.mCurrentPickingId = null;
        this.mInstantVisCheck = true;
    }
    showInteractors(pShow) {
        this.clear();
        this.mInteractors.enabled = pShow;
        if (!this.mInteractors.enabled || this.mGetInteractorsPromise != null)
            return;
        if (this.mMngr.app.model.selection.length > 1)
            return;
        this.mGetInteractorsPromise = this.getAndCreateInteractors().then(() => {
            this.mGetInteractorsPromise = null;
        }).catch((pError) => {
            this.mGetInteractorsPromise = null;
            Log.error("Failed to get oap interactors.", pError);
        });
    }
    dispose() {
        this.clearVisCheckTimeout();
        this.mInteractors.dispose();
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _requestVisibilityCheck(pInteractor) {
        if (this.mDisableRequestVisCheck)
            return;
        if (this.mInstantVisCheck) {
            if (this.mVisCheckTimeout == null)
                this.mVisCheckTimeout = window.setTimeout(this.onInstantVisibilityCheck, 0);
        }
        else {
            this.clearVisCheckTimeout();
            this.mVisCheckTimeout = window.setTimeout(this.onVisibilityCheck, 200);
        }
    }
    clear() {
        this.clearVisCheckTimeout();
        this.mInstantVisCheck = true;
        this.mCurrentPickingId = null;
        this.mInteractors.clear();
        this.mInteractors.enabled = false;
    }
    clearVisCheckTimeout() {
        if (this.mVisCheckTimeout != null) {
            clearTimeout(this.mVisCheckTimeout);
            this.mVisCheckTimeout = null;
        }
    }
    async getAndCreateInteractors() {
        await prepareSprites(getDataPath());
        const tInteractors = await this.mArticle.getMainArticle()._getOapInteractors(this.mArticle);
        //interactors were disabled while processing the asynchronous request
        if (!this.mInteractors.enabled)
            return;
        this.createInteractors(tInteractors);
    }
    createInteractors(pOapInteractors) {
        if (pOapInteractors == null)
            return;
        let tNeedVisCheck = false;
        for (const tOapInteractor of pOapInteractors) {
            //one interactor per symbol
            if (tOapInteractor.symbols != null && tOapInteractor.symbols.length > 0) {
                for (const tSymbol of tOapInteractor.symbols) {
                    const tHiddenMode = this.createInteractor(tOapInteractor, tSymbol);
                    tNeedVisCheck = tNeedVisCheck || tHiddenMode;
                }
            }
            else {
                const tHiddenMode = this.createInteractor(tOapInteractor);
                tNeedVisCheck = tNeedVisCheck || tHiddenMode;
            }
        }
        //prepare visibility check
        if (tNeedVisCheck) {
            this.mCurrentPickingId = Interactors.sPickingIDCounter++;
            const tNodes = this.mArticle.getMainArticle().getNodes(true);
            for (const tNode of tNodes) {
                if (tNode instanceof Mesh && tNode.geometry != null &&
                    tNode.isPickable && tNode.isVisible && tNode.isEnabled()) {
                    tNode[Interactors.PICKING_ID] = this.mCurrentPickingId;
                }
            }
        }
    }
    //returns true if interactor uses hidden mode
    createInteractor(pOapInteractor, pOapSymbol) {
        const tInteractor = new Interactor(this, pOapInteractor, pOapSymbol);
        this.mInteractors.addInteractor(tInteractor);
        return (tInteractor.hiddenMode);
    }
    checkInteractorVisibilities() {
        this.mDisableRequestVisCheck = true; //prevent recursive calls
        for (const tInter of this.mInteractors.interactors) {
            const tInteractor = tInter;
            if (!tInteractor.hiddenMode)
                continue;
            this.checkInteractorVisibility(tInteractor);
        }
        this.mDisableRequestVisCheck = false;
    }
    checkInteractorVisibility(pInteractor) {
        if (this.mInteractors.view == null)
            return;
        const tCameraVec = Interactor._GetDirectionToCamera(pInteractor.worldPosition, this.mInteractors.view);
        const tRay = new Ray(pInteractor.worldPosition, tCameraVec);
        const tResult = this.mInteractors.viewer.scene.pickWithRay(tRay, this.pickPredicate, true);
        pInteractor.setHidden(tResult != null && tResult.hit);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Interactors.PICKING_ID = "cf.OAPInteractors.pickingId";
Interactors.sPickingIDCounter = 0;
